import React, { useContext, useEffect, useState } from "react";
import Box from "@material-ui/core/Box";
import { Monitor, Activity, Edit, Calendar } from "react-feather";
import {
  Button,
  Grid,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Divider,
} from "@material-ui/core";
import Column from "../../components/Detail";
import EditEklok from "./EditEklok";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import moment from "moment";
import { Fragment } from "react";
import { ErrorCode } from "../../utils/errorCode";
import ActivationEklok from "./ActivationEklok";
import Delete from "./DeleteEklok";
import { UseMe } from "../../api/users/Me.api";
import { AuthContext } from "../../context/auth/AuthContext";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import EditReley from "./EditReleyEklok";
import SupportEclockOtp from "./SupportEklokOtp";

const useStyles = makeStyles((theme) => ({
  button: {
    width: "100%",
    maxWidth: "15rem",
  },
  rightButton: {
    minWidth: "9.5rem",
  },
  cell: {
    [theme.breakpoints.down("xs")]: {
      padding: "5px",
    },
  },
}));

const CustomTooltip = ({ active, payload }) => {
  const { t } = useTranslation();

  if (active && payload && payload.length) {
    return (
      <div>
        <p>
          {t("details_modal.modules_tooltip_name")}: {payload[0].payload?.NAME}
        </p>
        <p>
          {t("details_modal.modules_tooltip_percentage")}:{" "}
          {payload[0].payload?.PERCENTAGE}%
        </p>
        <p>
          {t("details_modal.modules_tooltip_hits")}: {payload[0].payload?.HITS}
        </p>
        <p>
          {t("details_modal.modules_tooltip_usage_time")}:{" "}
          {payload[0].payload?.USAGE_TIME}s
        </p>
        <p>
          {t("details_modal.modules_tooltip_type")}: {payload[0].payload?.TYPE}
        </p>
      </div>
    );
  }
  return null;
};

const Detail = ({ plant, eklok, onRefresh = () => {} }) => {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const [openEditEklok, setOpenEditEklok] = useState(false);
  const [openActivationEklok, setOpenActivationEklok] = useState(false);
  const [openDeleteEklok, setOpenDeleteEklok] = useState(false);
  const { content: user } = UseMe(token);
  const [showDelete, setShowDelete] = useState(false);
  const { t } = useTranslation();
  const [showReley, setShowReley] = useState(false);

  const role = user.groups[0].name;

  const getActivity = (eklok) => [
    {
      title: t("details_modal.activity_connected") + ":",
      value: eklok.connected
        ? t("statuses.connected")
        : t("statuses.unconnected"),
    },
  ];

  const getDevice = (eklok) => [
    { title: t("details_modal.eklok_name"), value: eklok.name },
    { title: t("details_modal.eklok_hmi_version"), value: eklok.hmi_version },
    { title: t("details_modal.eklok_hw_id"), value: eklok.hw_id },
    { title: t("details_modal.eklok_serial_id"), value: eklok.serial_id },
    {
      title: t("details_modal.eklok_middleware_version"),
      value: eklok.middleware_version,
    },
    {
      title: t("details_modal.eklok_software_version"),
      value: eklok.software_version,
    },
    { title: t("details_modal.eklok_model_type"), value: eklok.model_type },
  ];

  const getCreatedBy = (eklok) => [
    {
      title: t("details_modal.plant_user"),
      value: `${eklok.created_by?.first_name} ${eklok.created_by?.last_name}`,
    },
    { title: t("details_modal.plant_email"), value: eklok.created_by?.email },
  ];

  const getUpdateAt = (eklok) => [
    {
      title: t("details_modal.plant_user"),
      value: eklok.update_at
        ? `${eklok.update_at.first_name} ${eklok.update_at.last_name}`
        : "",
    },
    {
      title: t("details_modal.plant_email"),
      value: eklok.update_at ? eklok.update_at.email : "",
    },
    {
      title: t("details_modal.plant_date"),
      value: moment(eklok.updated_at).format("YYYY-MM-DD H:mm:ss"),
    },
  ];

  useEffect(() => onRefresh(), [openEditEklok]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (user && user.groups) {
      switch (role) {
        case "Admin":
          setShowDelete(true);
          setShowReley(true);
          break;
        case "Installer":
          setShowReley(true);
          break;
        default:
          setShowDelete(false);
          break;
      }
    }
  }, [user, role]);

  const getM80 = (data) => {
    return data >= 80 ? 80 : data;
  };

  const getM90 = (data) => {
    return data >= 80 && data <= 100 ? data - 80 : data < 80 ? 0 : 20;
  };

  const getM100 = (data) => {
    return data >= 100 ? data - 100 : 0;
  };

  const getBarData = (data) => {
    const a = data.map((d) => {
      return {
        ...d,
        ...{
          m80: getM80(d["PERCENTAGE"]),
          m90: getM90(d["PERCENTAGE"]),
          m100: getM100(d["PERCENTAGE"]),
        },
      };
    });
    return a;
  };

  const getAlarmStatus = (s) => {
    return s.toUpperCase() === "ACTIVE"
      ? t("details_modal.alarms_status_active")
      : t("details_modal.alarms_status_inactive");
  };

  return (
    <Fragment>
      <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={2} mt={2}>
        <Box mb={2}>
          <Typography variant="h4">
            {t("details_modal.device_title")}
          </Typography>
        </Box>
        <Grid container>
          <Grid item xs={12} sm={12} md={12} lg={4}>
            <Typography variant="h5">
              {t("details_modal.created_by")}
            </Typography>
            <Column icon={<Calendar />} data={getCreatedBy(eklok)} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Typography variant="h5">
              {t("details_modal.updated_by")}
            </Typography>
            <Column icon={<Edit />} data={getUpdateAt(eklok)} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4}>
            <Typography variant="h5">{t("details_modal.info")}</Typography>
            <Column icon={<Monitor />} data={getDevice(eklok)} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5">{t("details_modal.activity")}</Typography>
            <Column icon={<Activity />} data={getActivity(eklok)} />
          </Grid>
          <Grid container item xs={12} md={7} lg={5} spacing={2}>
            <Grid item xs={12} sm={4} align="center">
              <Button
                className={classes.button}
                color="primary"
                variant="contained"
                onClick={() => setOpenEditEklok(true)}
              >
                {t("details_modal.edit_eklok")}
              </Button>
            </Grid>
            {showDelete && (
              <Grid item xs={12} sm={4} align="center">
                <Button
                  className={classes.button}
                  color="primary"
                  variant="contained"
                  onClick={() => setOpenDeleteEklok(true)}
                >
                  {t("details_modal.delete_eklok")}
                </Button>
              </Grid>
            )}
            <Grid item xs={12} sm={4} align="center">
              <Button
                className={clsx(classes.button, classes.rightButton)}
                color="primary"
                variant="contained"
                onClick={() => setOpenActivationEklok(true)}
              >
                {t("details_modal.generate_code")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {eklok.last_mqtt_json?.OUTPUT_STATUS?.length > 0 ? (
        <>
          <Divider />
          <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={2} mt={2}>
            <Grid container>
              <Grid item xs={12} sm={6}>
                <Box mb={2}>
                  <Typography variant="h4">
                    {t("details_modal.module_status")}
                  </Typography>
                </Box>
                <ResponsiveContainer
                  height={50 * eklok.last_mqtt_json?.OUTPUT_STATUS.length}
                  width={window.innerWidth / 1.4}
                >
                  <BarChart
                    data={getBarData(eklok.last_mqtt_json?.OUTPUT_STATUS)}
                    layout={"vertical"}
                  >
                    <Tooltip content={<CustomTooltip />} />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="NAME" />
                    <Bar dataKey="m80" stackId="ag" fill="#82ca9d" />
                    <Bar dataKey="m90" stackId="ag" fill="#ebd334" />
                    <Bar dataKey="m100" stackId="ag" fill="#eb4034" />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          </Box>
          <Divider />
        </>
      ) : null}

      {showReley &&
        eklok.last_mqtt_json?.OUTPUT_STATUS &&
        eklok.last_mqtt_json?.OUTPUT_STATUS.length > 0 && (
          <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={2} mt={2}>
            <EditReley
              onRefresh={onRefresh}
              reley={eklok.last_mqtt_json.OUTPUT_STATUS}
              eklok={eklok}
            ></EditReley>
          </Box>
        )}

      <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={2} mt={2}>
        <Grid container>
          {eklok.alarm_list ? (
            <Grid item xs={12} sm={12}>
              <Typography variant="h5">
                {t("details_modal.alarms_title")}
              </Typography>
              <Table aria-label="alarm table">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.cell}>
                      {t("details_modal.alarms_code")}
                    </TableCell>
                    <TableCell>{t("details_modal.alarms_status")}</TableCell>
                    <TableCell>{t("details_modal.alarms_time")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {eklok.alarm_list.map((row, i) => (
                    <TableRow key={`${row.ALARM_CODE}_${i}`}>
                      <TableCell component="th" scope="row">
                        {t(`alarms_eklok.${ErrorCode[row.ALARM_CODE]}`)}
                        {row.ALARM_SUBCODE ? ` - ${row.ALARM_SUBCODE}` : null}
                      </TableCell>
                      <TableCell>{getAlarmStatus(row.ALARM_STATUS)}</TableCell>
                      <TableCell>
                        {moment.unix(row.ALARM_TS).format("YYYY-MM-DD H:mm:ss")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          ) : null}
        </Grid>
      </Box>
      {openEditEklok && eklok ? (
        <EditEklok
          handleClose={() => setOpenEditEklok(false)}
          open={openEditEklok}
          eklok={{ ...eklok, plant: plant.id }}
        />
      ) : null}
      {role !== "Support" && openActivationEklok && eklok ? (
        <ActivationEklok
          handleClose={() => setOpenActivationEklok(false)}
          open={openActivationEklok}
          eklok={{ ...eklok, plant: plant.id }}
        />
      ) : null}
      {role === "Support" && openActivationEklok && eklok ? (
        <SupportEclockOtp
          handleClose={() => setOpenActivationEklok(false)}
          open={openActivationEklok}
          eklok={{ ...eklok, plant: plant.id }}
        />
      ) : null}
      {openDeleteEklok && eklok ? (
        <Delete
          handleClose={() => setOpenDeleteEklok(false)}
          open={openDeleteEklok}
          eklok={eklok}
        />
      ) : null}
    </Fragment>
  );
};

export default Detail;
